import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSectionBase } from '../../Constant/useActiveLibrary';
import Swal from '../librarianSwal';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';

import {
  getBookById,
  getBookCopiesByBookId,
  getErrorMessage
} from '../../api/api';
import '../BookList.css';
import '../lib-page-header.css';
import Pagination from '../components/Pagination';

const PAGE_SIZE = 10;

const BookDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sectionBase = useSectionBase();

  const [book, setBook] = useState(null);
  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [page, setPage] = useState(1);

  const fetchBookDetails = async () => {
    try {
      setLoading(true);

      const bookData = await getBookById(id);
      const copyData = await getBookCopiesByBookId(id);

      setBook(bookData);
      setCopies(copyData || []);
    } catch (error) {
      console.error('Book details error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBookDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setPage(1);
  }, [id]);

  const totalPages = Math.max(1, Math.ceil(copies.length / PAGE_SIZE));
  const paginatedCopies = copies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  /* ── Modern colour mapping ── */
  const statusBadge = (status) => {
    if (status === 'active') return 'book-badge-success';
    if (status === 'inactive') return 'book-badge-secondary';
    return 'book-badge-light';
  };

  const availabilityColor = (status) => {
    if (status === 'available') return 'green';
    if (status === 'issued') return 'blue';
    if (status === 'reserved') return 'amber';
    if (status === 'lost' || status === 'damaged') return 'red';
    if (status === 'discarded') return 'slate';
    return 'slate';
  };

  const conditionColor = (status) => {
    if (status === 'good') return 'green';
    if (status === 'damaged') return 'red';
    if (status === 'lost') return 'slate';
    return 'slate';
  };

  if (showLoader) {
    return (
      <div className="bd-page">
        <Loader />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="bd-page">
        <div className="bd-state">
          <p style={{ margin: '0 0 14px' }}>Book not found</p>
          <button className="bd-btn bd-btn-back" onClick={() => navigate(`${sectionBase}/books`)}>
            <i className="ti ti-arrow-left" /> Back to list
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bd-page">

      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-info-circle" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Book Details</h3>
            <div className="lib-page-header__subtitle">View book master and copy details</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button className="bd-btn bd-btn-edit" onClick={() => navigate(`${sectionBase}/books/${id}/edit`)}>
            <i className="ti ti-edit" /> Edit Book
          </button>
          <button className="bd-btn bd-btn-back" onClick={() => navigate(`${sectionBase}/books`)}>
            <i className="ti ti-arrow-left" /> Back
          </button>
        </div>
      </div>


       <div className="bd-stats">
        <div className="bd-stat bd-stat-slate">
          <div className="bd-stat-ico"><i className="ti ti-books" /></div>
          <div className="bd-stat-text">
            <div className="bd-stat-label">Total Copies</div>
            <div className="bd-stat-val">{book.total_copies || 0}</div>
          </div>
        </div>
        <div className="bd-stat bd-stat-green">
          <div className="bd-stat-ico"><i className="ti ti-circle-check" /></div>
          <div className="bd-stat-text">
            <div className="bd-stat-label">Available</div>
            <div className="bd-stat-val">{book.available_copies || 0}</div>
          </div>
        </div>
        <div className="bd-stat bd-stat-blue">
          <div className="bd-stat-ico"><i className="ti ti-arrow-up-right" /></div>
          <div className="bd-stat-text">
            <div className="bd-stat-label">Issued</div>
            <div className="bd-stat-val">{book.issued_copies || 0}</div>
          </div>
        </div>
        <div className="bd-stat bd-stat-amber">
          <div className="bd-stat-ico"><i className="ti ti-bookmark" /></div>
          <div className="bd-stat-text">
            <div className="bd-stat-label">Reserved</div>
            <div className="bd-stat-val">{book.reserved_copies || 0}</div>
          </div>
        </div>
      </div>

      {/* ── Book Information ── */}
      <div className="book-detail-info">
      <div className="bd-card">
        <div className="bd-card-head">
          <i className="ti ti-book-2" /> Book Information
        </div>
        <div className="bd-card-body">
          <div className="bd-info-grid">
            <div className="bd-field">
              <span className="bd-label">Title</span>
              <span className="bd-value strong">{book.title || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Subtitle</span>
              <span className="bd-value">{book.subtitle || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Category</span>
              <span className="bd-value">{book.category_name || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Author</span>
              <span className="bd-value">{book.author_name || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Publisher</span>
              <span className="bd-value">{book.publisher_name || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">ISBN</span>
              <span className="bd-value">{book.isbn || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Edition</span>
              <span className="bd-value">{book.edition || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Language</span>
              <span className="bd-value">{book.language || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Publish Year</span>
              <span className="bd-value">{book.publish_year || '-'}</span>
            </div>
            <div className="bd-field">
              <span className="bd-label">Status</span>
              <span>
                <span className={`book-status ${statusBadge(book.status)}`}>
                  {book.status || '-'}
                </span>
              </span>
            </div>
            <div className="bd-field bd-field-full">
              <span className="bd-label">Description</span>
              <span className="bd-value">{book.description || '-'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stat cards ── */}
     

      {/* ── Book Copies ── */}
      <div className="bd-card">
        <div className="bd-card-head bd-card-head-row">
          <span><i className="ti ti-copy" /> Book Copies</span>
          <button
            className="bd-btn bd-btn-primary"
            onClick={() => navigate(`${sectionBase}/book-copies/add?book_id=${book.id}`)}
          >
            <i className="ti ti-plus" /> Add Copy
          </button>
        </div>
        <div className="bd-card-body bd-card-body-flush">
          <div className="book-table-wrapper">
            <table className="book-table bd-copies-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Accession No</th>
                  <th>Barcode</th>
                  <th>Copy No</th>
                  <th>Location</th>
                  <th>Condition</th>
                  <th>Availability</th>
                  <th>Reference</th>
                  <th>Discarded</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCopies.length > 0 ? (
                  paginatedCopies.map((copy, index) => (
                    <tr key={copy.id}>
                      <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                      <td>{copy.accession_no || '-'}</td>
                      <td>{copy.barcode || '-'}</td>
                      <td>{copy.copy_number || '-'}</td>
                      <td>
                        <div className="bd-loc-main">Room: {copy.room_no || '-'}</div>
                        <div className="bd-loc-sub">
                          Almirah: {copy.almirah_no || '-'} | Rack: {copy.rack_no || '-'} | Shelf: {copy.shelf_no || '-'}
                        </div>
                      </td>
                      <td>
                        <span className={`bd-pill ${conditionColor(copy.condition_status)}`}>
                          {copy.condition_status || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`bd-pill ${availabilityColor(copy.availability_status)}`}>
                          {copy.availability_status || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`bd-pill ${copy.is_reference_only ? 'amber' : 'slate'}`}>
                          {copy.is_reference_only ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <span className={`bd-pill ${copy.is_discarded ? 'red' : 'slate'}`}>
                          {copy.is_discarded ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>
                        <div className="book-action-buttons">
                          <button
                            className="book-action-btn edit"
                            onClick={() => navigate(`${sectionBase}/book-copies/${copy.id}/edit`)}
                          >
                            <i className="ti ti-edit" /> Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="book-empty">No copies found</td>
                  </tr>
                )}
              </tbody>
            </table>

            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={copies.length}
              itemLabel="copies"
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default BookDetails;
